import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import {
  resolveAcademicRecord,
  resolveFeatureFlag,
  resolveCandidateActiveYears,
  resolveNearGraduationMaxCourses,
  resolveGradingScale,
  resolveStudentCodeFormat,
  isStudentCodeConfigured,
  type FeatureFlagKey,
  type GradingScale,
  type StudentCodeFormat,
  type TenantModules,
} from '@dosfilos/domain';
import { db } from '../lib/firebase';
import { contactOf, type PortalContact } from '../pages/portal-v2/home/portal-home.model';
import { isPathEnabled } from '../config/modules.config';
import { useTenantStore } from '../stores/tenant.store';

// ─── La configuración del seminario que cambia lo que se MUESTRA ───────────
// Los flags de plataforma (`config.enabledFeatures`), el idioma del seminario
// (`config.locale`), la escala del expediente (`config.transcriptScale`) y el
// contacto del seminario que muestra el inicio del portal (`config.landing.contact`).
//
// MIENTRAS CARGA, TODO FLAG CUENTA COMO APAGADO: una pantalla que esconde algo
// detrás de un flag muestra lo de siempre hasta saber que está encendido. Para
// los seminarios sin el flag —todos menos los que lo piden— nada cambia nunca.

export interface TenantConfigView {
  enabledFeatures: Partial<Record<string, boolean>>;
  locale: string | null;
  /** `config.isNetwork`: una RED (p. ej. la de revisión de TMAI), no un seminario. */
  isNetwork: boolean;
  /** M4·D1: `config.academicRecord` crudo (se resuelve abajo). */
  academicRecord: { mode?: 'courses' | 'credentials'; imported?: boolean } | null;
  transcriptScale: 'letter-4' | null;
  /** M2·H11: N de «activo» en Candidatos, resuelto (5 por defecto). */
  candidateActiveYears: number;
  /** Candidatos: «cerca» = faltan N cursos o menos, resuelto (4 por defecto). */
  nearGraduationMaxCourses: number;
  /** LA escala de notas del seminario, resuelta (grading-scale.ts). */
  gradingScale: GradingScale;
  /** El formato del código de alumno (H6), resuelto. */
  studentCodeFormat: StudentCodeFormat;
  /** ¿El seminario guardó su formato? Sin eso no se asigna ningún código. */
  studentCodeConfigured: boolean;
  contact: PortalContact | null;
  /** Los módulos que usa el seminario (clave ausente = encendido). */
  modules: TenantModules;
}

export function useTenantConfig() {
  const { currentTenantId } = useTenantStore();
  const query = useQuery({
    queryKey: ['tenant-config-view', currentTenantId],
    enabled: !!currentTenantId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<TenantConfigView> => {
      const snap = await getDoc(doc(db, `tenants/${currentTenantId}`));
      const config = (snap.data()?.['config'] ?? {}) as Record<string, unknown>;
      return {
        enabledFeatures: (config['enabledFeatures'] ?? {}) as Partial<Record<string, boolean>>,
        locale: typeof config['locale'] === 'string' ? config['locale'] : null,
        isNetwork: config['isNetwork'] === true,
        academicRecord:
          config['academicRecord'] && typeof config['academicRecord'] === 'object'
            ? (config['academicRecord'] as TenantConfigView['academicRecord'])
            : null,
        transcriptScale: config['transcriptScale'] === 'letter-4' ? 'letter-4' : null,
        candidateActiveYears: resolveCandidateActiveYears(
          config as Parameters<typeof resolveCandidateActiveYears>[0],
        ),
        nearGraduationMaxCourses: resolveNearGraduationMaxCourses(
          config as Parameters<typeof resolveNearGraduationMaxCourses>[0],
        ),
        gradingScale: resolveGradingScale(config as Parameters<typeof resolveGradingScale>[0]),
        studentCodeFormat: resolveStudentCodeFormat(
          config as Parameters<typeof resolveStudentCodeFormat>[0],
        ),
        studentCodeConfigured: isStudentCodeConfigured(
          config as Parameters<typeof isStudentCodeConfigured>[0],
        ),
        contact: contactOf(config),
        modules: (config['modules'] ?? {}) as TenantModules,
      };
    },
  });
  const data = query.data;
  return {
    loaded: !!data,
    /** La lectura terminó (bien o con error): ya se puede decidir con los flags.
     *  Un error cuenta como «flag apagado», nunca deja la pantalla esperando. */
    settled: !currentTenantId || query.isFetched,
    locale: data?.locale ?? null,
    isNetwork: data?.isNetwork ?? false,
    /** M4·D1: cómo lleva el expediente el seminario. Mientras carga, el de
     *  siempre (por cursos, sin importar), igual que un flag apagado. */
    academicRecord: resolveAcademicRecord(data ? { academicRecord: data.academicRecord } : null),
    transcriptScale: data?.transcriptScale ?? null,
    /** Mientras carga, el valor por defecto. */
    candidateActiveYears: data?.candidateActiveYears ?? resolveCandidateActiveYears(null),
    nearGraduationMaxCourses:
      data?.nearGraduationMaxCourses ?? resolveNearGraduationMaxCourses(null),
    /** Mientras carga, la de siempre (1-7). */
    gradingScale: data?.gradingScale ?? resolveGradingScale(null),
    studentCodeFormat: data?.studentCodeFormat ?? resolveStudentCodeFormat(null),
    studentCodeConfigured: data?.studentCodeConfigured ?? false,
    contact: data?.contact ?? null,
    /** ¿Se muestra esta ruta? Mientras carga, sí: quien la usa espera `settled`
     *  para no mostrar y después esconder (ver modules.config). */
    pathEnabled: (path: string): boolean => isPathEnabled(data?.modules, path),
    /** Los módulos guardados (clave ausente = encendido; ver isModuleEnabled). */
    modules: data?.modules ?? {},
    featureEnabled: (key: FeatureFlagKey): boolean =>
      !!data && resolveFeatureFlag(data.enabledFeatures, key),
  };
}
